import React, { useState, useEffect, memo } from 'react';
import { Bell, CheckCircle2, X, Zap } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';

interface RecentAchievement {
  id: string;
  name: string;
  description: string;
  difficulty: string;
  xpReward: number;
  earnedDate: string;
  icon?: string;
}

interface AchievementNotificationCenterProps {
  recentAchievements: RecentAchievement[];
}

const getDifficultyColor = (difficulty: string) => {
  switch(difficulty.toLowerCase()) {
    case 'bronze': return 'bg-[#CD7F32]/20 text-[#8B4513]';
    case 'silver': return 'bg-[#C0C0C0]/30 text-[#71797E]';
    case 'gold': return 'bg-[#FFD700]/30 text-[#B8860B]';
    case 'platinum': return 'bg-[#90C3D4]/30 text-[#0066CC]';
    default: return 'bg-muted text-muted-foreground';
  }
};

export const AchievementNotificationCenter = memo<AchievementNotificationCenterProps>(({
  recentAchievements
}) => {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);

  // Calculate unread based on achievements not yet dismissed
  const unreadCount = recentAchievements.filter(a => !dismissedIds.has(a.id)).length;

  const markAsRead = (achievementId: string) => {
    setDismissedIds(prev => new Set([...prev, achievementId]));
  };

  const markAllAsRead = () => {
    setDismissedIds(new Set(recentAchievements.map(a => a.id)));
  };

  // Reset dismissed when new achievements come in
  useEffect(() => {
    const currentIds = new Set(recentAchievements.map(a => a.id));
    setDismissedIds(prev => {
      const newSet = new Set<string>();
      prev.forEach(id => {
        if (currentIds.has(id)) {
          newSet.add(id);
        }
      });
      return newSet;
    });
  }, [recentAchievements]);

  if (recentAchievements.length === 0) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          aria-label={`${unreadCount} unread achievement notifications`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg">Recent Achievements</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="text-xs"
            >
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-96">
          {recentAchievements.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No recent achievements</p>
            </div>
          ) : (
            <div className="divide-y">
              {recentAchievements.map((achievement) => {
                const isUnread = !dismissedIds.has(achievement.id);
                
                return (
                  <div
                    key={achievement.id}
                    className={`p-4 transition-colors hover:bg-accent/50 ${
                      isUnread ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="h-10 w-10 rounded-full bg-success/20 flex items-center justify-center">
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-sm leading-tight">
                            {achievement.name}
                          </h4>
                          {isUnread && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              onClick={() => markAsRead(achievement.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {achievement.description}
                        </p>
                        
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${getDifficultyColor(achievement.difficulty)}`}
                          >
                            {achievement.difficulty}
                          </Badge>
                          
                          {achievement.xpReward > 0 && (
                            <div className="flex items-center gap-1 text-xs text-primary">
                              <Zap className="h-3 w-3" />
                              <span className="font-semibold">+{achievement.xpReward} XP</span>
                            </div>
                          )}
                          
                          <span className="text-xs text-muted-foreground">
                            {achievement.earnedDate && format(new Date(achievement.earnedDate), 'MMM d, h:mm a')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
});

AchievementNotificationCenter.displayName = 'AchievementNotificationCenter';
